import type { SWRConfiguration } from 'swr'

export type CachePreset = 'STATIC' | 'SHORT' | 'REALTIME' | 'NONE'

export type CachePolicy = {
  readonly preset: CachePreset
  readonly revalidate?: number | false
  readonly tags?: readonly string[]
  readonly serverCache?: 'force-cache' | 'no-store'
  readonly clientCache?: RequestCache
  readonly swr?: Readonly<SWRConfiguration>
}

export type CachePolicyInput = CachePreset | CachePolicy

// 서버 Action 경유 시 함수 직렬화가 불가하므로 swr 필드 제외
export type ServerCachePolicyInput =
  | CachePreset
  | Pick<CachePolicy, 'preset' | 'revalidate' | 'tags' | 'serverCache'>

/**
 * 전역 캐시 정책 프리셋
 * - STATIC  : 거의 변하지 않음 (1h revalidate)
 * - SHORT   : 짧은 TTL (60s)
 * - REALTIME: 안전 fallback, 항상 최신 (no-store, SWR 1초 중복 제거)
 * - NONE    : wrapper가 cache option을 지정하지 않아 framework 기본값 사용
 */
export const CACHE_POLICIES: Readonly<Record<CachePreset, CachePolicy>> =
  Object.freeze({
    STATIC: createCachePolicy({
      preset: 'STATIC',
      revalidate: 3600,
      serverCache: 'force-cache',
      clientCache: 'default',
      swr: {
        dedupingInterval: 3_600_000,
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
      },
    }),
    SHORT: createCachePolicy({
      preset: 'SHORT',
      revalidate: 60,
      serverCache: 'force-cache',
      clientCache: 'default',
      swr: {
        dedupingInterval: 60_000,
        revalidateOnFocus: true,
        revalidateOnMount: true,
        revalidateOnReconnect: true,
        revalidateIfStale: true,
      },
    }),
    REALTIME: createCachePolicy({
      preset: 'REALTIME',
      serverCache: 'no-store',
      clientCache: 'no-store',
      swr: {
        dedupingInterval: 1_000,
        revalidateOnFocus: true,
        revalidateOnMount: true,
        revalidateOnReconnect: true,
        revalidateIfStale: true,
      },
    }),
    NONE: createCachePolicy({
      preset: 'NONE',
    }),
  })

/**
 * preset 이름 또는 partial policy를 완전한 CachePolicy로 정규화
 * - 문자열 preset → 프리셋 반환
 * - 객체 입력 → 프리셋 기반 + 사용자 override 병합
 */
export function resolvePolicy(
  input: CachePolicyInput | ServerCachePolicyInput | undefined
): CachePolicy {
  if (input === undefined) return CACHE_POLICIES.REALTIME
  if (typeof input === 'string') return CACHE_POLICIES[input]

  if (
    input.preset === 'NONE' &&
    Object.keys(input).some((key) => key !== 'preset')
  ) {
    throw new Error('NONE cache preset은 override와 함께 사용할 수 없습니다.')
  }

  const base = CACHE_POLICIES[input.preset]
  const swrOverride = 'swr' in input ? input.swr : undefined
  const swr =
    base.swr || swrOverride ? { ...base.swr, ...swrOverride } : undefined

  return createCachePolicy({
    ...base,
    ...input,
    ...(swr ? { swr } : {}),
  })
}

function createCachePolicy(policy: CachePolicy): CachePolicy {
  return Object.freeze({
    ...policy,
    ...(policy.tags ? { tags: Object.freeze([...policy.tags]) } : {}),
    ...(policy.swr ? { swr: Object.freeze({ ...policy.swr }) } : {}),
  })
}
